import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Field, Input, Select } from '@/components/ui/Field'
import { Section } from '@/components/ui/Page'
import { useCurrentStaff } from '@/features/auth/RequireStaff'
import { useContacts, type ContactWithJobs } from '@/features/contacts/queries'
import { useCreateQuestionnaire, useQuestionnaireTemplates } from '@/features/questionnaires/queries'
import { usePackages } from '@/features/quotes/queries'
import { useSettings } from '@/features/settings/useSettings'
import { cn } from '@/lib/cn'
import { formatDateShort, type IsoDate } from '@/lib/dates'
import { formatMoneyWhole } from '@/lib/format'
import { JOB_KIND_LABEL, type JobKind } from '@/lib/jobs'
import { describeError } from '@/lib/supabase'
import {
  createContract,
  createInvoice,
  createQuote,
  resolveProject,
  useCreateClient,
  type DocumentKind,
  type ProjectChoice,
} from './create'

const NOUN: Record<DocumentKind, string> = { quote: 'quote', invoice: 'invoice', contract: 'contract', questionnaire: 'questionnaire' }
const ROUTE: Record<DocumentKind, string> = { quote: '/quotes', invoice: '/invoices', contract: '/contracts', questionnaire: '/questionnaires' }
const PROJECT_KINDS: JobKind[] = ['wedding', 'event', 'lifestyle', 'brand']

const fullName = (contact: { first_name: string; last_name: string | null }) =>
  [contact.first_name, contact.last_name].filter(Boolean).join(' ')
const initials = (contact: { first_name: string; last_name: string | null }) =>
  `${contact.first_name.charAt(0)}${contact.last_name?.charAt(0) ?? ''}`.toUpperCase()

/**
 * New quote, invoice, contract or questionnaire (29 Oct 2026), the way
 * Pixieset does it, on one page: choose the client (or add one here), the
 * project (one of theirs, a new one, or none), then start blank or from a
 * template. Choosing how to start makes the document and opens it.
 */
export function NewDocument({ kind, jobId, onClose }: { kind: DocumentKind; jobId: string | null; onClose: () => void }) {
  const contacts = useContacts(false)
  const navigate = useNavigate()
  const { staff } = useCurrentStaff()
  const settings = useSettings()
  const packages = usePackages()
  const questionnaireTemplates = useQuestionnaireTemplates()
  const createQuestionnaire = useCreateQuestionnaire()
  const [busy, setBusy] = useState<string | null>(null)

  // From a project ("Send a questionnaire", "Build a quote"): its client and the project are filled in.
  const fromProject = useMemo(
    () => (jobId ? (contacts.data ?? []).find((contact) => contact.jobs.some((job) => job.id === jobId)) : undefined),
    [contacts.data, jobId],
  )
  const [clientId, setClientId] = useState<string | null>(null)
  const [project, setProject] = useState<ProjectChoice | null>(null)
  useEffect(() => {
    if (fromProject && clientId === null && jobId) {
      setClientId(fromProject.id)
      setProject({ mode: 'existing', jobId })
    }
  }, [fromProject, clientId, jobId])

  const client = (contacts.data ?? []).find((contact) => contact.id === clientId) ?? null
  const projectReady =
    project !== null && (project.mode !== 'new' || (project.name.trim() !== '' && project.date !== ''))
  const ready = client !== null && projectReady

  type Start = { key: string; label: string; detail?: string; make: (contactId: string, projectId: string | null) => Promise<string> }
  const blank: Start = {
    key: 'blank',
    label: `Blank ${NOUN[kind]}`,
    make: (contactId, projectId) => {
      if (kind === 'quote')
        return createQuote({ contactId, jobId: projectId, staffId: staff.id, validityDays: settings.data?.default_quote_validity_days ?? 14, fromPackage: null })
      if (kind === 'invoice') return createInvoice({ contactId, jobId: projectId, staffId: staff.id })
      if (kind === 'contract') return createContract({ contactId, jobId: projectId, staffId: staff.id, title: 'Contract' })
      return createQuestionnaire.mutateAsync({
        contactId,
        jobId: projectId,
        template: { id: null, name: 'Questionnaire', intro: null, questions: [] },
      })
    },
  }
  const templates: Start[] =
    kind === 'quote'
      ? (packages.data ?? []).map((pkg) => ({
          key: pkg.id,
          label: pkg.name,
          detail: formatMoneyWhole(Number(pkg.base_price)),
          make: (contactId, projectId) =>
            createQuote({ contactId, jobId: projectId, staffId: staff.id, validityDays: settings.data?.default_quote_validity_days ?? 14, fromPackage: pkg }),
        }))
      : kind === 'questionnaire'
        ? (questionnaireTemplates.data ?? [])
            .filter((template) => !template.archived)
            .map((template) => ({
              key: template.id,
              label: template.name,
              detail: `${template.questions.filter((question) => question.type !== 'note').length} questions`,
              make: (contactId, projectId) => createQuestionnaire.mutateAsync({ contactId, jobId: projectId, template }),
            }))
        : []

  async function start(choice: Start) {
    if (!client || !project || !projectReady) return
    setBusy(choice.key)
    try {
      const projectId = await resolveProject(project, client.id, staff.id)
      const id = await choice.make(client.id, projectId)
      navigate(`${ROUTE[kind]}/${id}`)
    } catch (error) {
      toast.error(`Could not make the ${NOUN[kind]}`, { description: describeError(error) })
      setBusy(null)
    }
  }

  return (
    <Section title={`Create a new ${NOUN[kind]}`} actions={<Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>}>
      <div className="flex max-w-[40rem] flex-col gap-5">
        <ClientPicker
          contacts={contacts.data ?? []}
          loading={contacts.isPending}
          client={client}
          onChange={(id) => {
            setClientId(id)
            setProject(null)
          }}
        />
        {client ? <ProjectPicker client={client} value={project} onChange={setProject} /> : null}

        <div className={cn('flex flex-col gap-2', !ready && 'pointer-events-none opacity-40')} aria-disabled={!ready}>
          <p className="label">How would you like to start?</p>
          <StartRow icon="＋" label={blank.label} busy={busy === blank.key} onClick={() => void start(blank)} />
          <p className="label mt-3">Start with a template</p>
          {templates.length ? (
            templates.map((template) => (
              <StartRow
                key={template.key}
                icon="▤"
                label={template.label}
                {...(template.detail ? { detail: template.detail } : {})}
                busy={busy === template.key}
                onClick={() => void start(template)}
              />
            ))
          ) : (
            <p className="text-[length:var(--text-sm)] text-taupe">No {NOUN[kind]} templates yet.</p>
          )}
          {!ready ? (
            <p className="text-[length:var(--text-2xs)] text-taupe">
              {client ? 'Choose a project, a new project, or No project first.' : 'Choose the client first.'}
            </p>
          ) : null}
        </div>
      </div>
    </Section>
  )
}

function StartRow({ icon, label, detail, busy, onClick }: { icon: string; label: string; detail?: string; busy: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className="flex items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-[var(--color-wash-strong)] disabled:opacity-60"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-wash-strong)] text-[length:var(--text-base)]" aria-hidden>
        {icon}
      </span>
      <span className="text-[length:var(--text-sm)]">{busy ? 'Opening…' : label}</span>
      {detail ? <span className="text-[length:var(--text-2xs)] text-taupe">{detail}</span> : null}
    </button>
  )
}

/** A search box with a list under it; extra rows (Add new …) sit at the bottom. */
function SearchMenu({
  placeholder,
  items,
  footer,
  onPick,
}: {
  placeholder: string
  items: Array<{ id: string; label: string; detail?: string }>
  footer?: ReactNode
  onPick: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])
  const needle = text.trim().toLowerCase()
  const shown = needle ? items.filter((item) => `${item.label} ${item.detail ?? ''}`.toLowerCase().includes(needle)) : items

  return (
    <div ref={box} className="relative">
      <Input placeholder={placeholder} value={text} onFocus={() => setOpen(true)} onChange={(e) => (setText(e.target.value), setOpen(true))} />
      {open ? (
        <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-[22rem] overflow-y-auto rounded-md bg-[var(--surface-card)] py-1 shadow-[var(--shadow-card)] ring-1 ring-[var(--border-hairline)]">
          {shown.map((item) => (
            <button
              key={item.id}
              type="button"
              className="flex w-full items-baseline gap-2 px-3 py-2 text-left text-[length:var(--text-sm)] hover:bg-[var(--color-wash-strong)]"
              onClick={() => {
                onPick(item.id)
                setOpen(false)
                setText('')
              }}
            >
              {item.label}
              {item.detail ? <span className="text-[length:var(--text-2xs)] text-taupe">{item.detail}</span> : null}
            </button>
          ))}
          {shown.length === 0 ? <p className="px-3 py-2 text-[length:var(--text-sm)] text-taupe">Nothing matches.</p> : null}
          {footer ? (
            <div className="border-t" onClick={() => setOpen(false)}>
              {footer}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function MenuAction({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button type="button" className="flex w-full items-center gap-3 px-3 py-2 text-left text-[length:var(--text-sm)] hover:bg-[var(--color-wash-strong)]" onClick={onClick}>
      <span className="flex size-7 items-center justify-center rounded-full bg-[var(--color-wash-strong)]" aria-hidden>
        {icon}
      </span>
      {label}
    </button>
  )
}

function Chip({ text, mark, onClear }: { text: string; mark?: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-[var(--color-wash-strong)] py-1 pr-2 pl-1 text-[length:var(--text-sm)]">
      {mark ? (
        <span className="flex size-7 items-center justify-center rounded-full bg-[var(--surface-card)] text-[length:var(--text-2xs)] font-semibold">{mark}</span>
      ) : null}
      {text}
      <button type="button" aria-label="Change" className="px-1 text-taupe hover:text-ink" onClick={onClear}>
        ×
      </button>
    </span>
  )
}

function ClientPicker({
  contacts,
  loading,
  client,
  onChange,
}: {
  contacts: ContactWithJobs[]
  loading: boolean
  client: ContactWithJobs | null
  onChange: (id: string | null) => void
}) {
  const [adding, setAdding] = useState(false)
  const create = useCreateClient()
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' })

  if (client)
    return (
      <Field label="Client">
        <div>
          <Chip text={fullName(client)} mark={initials(client)} onClear={() => onChange(null)} />
        </div>
      </Field>
    )

  if (adding)
    return (
      <div className="flex flex-col gap-3 rounded-md bg-paper p-3">
        <p className="label">New client</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name">
            <Input autoFocus value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </Field>
          <Field label="Surname">
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Phone">
            <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={!form.first_name.trim()}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                {
                  first_name: form.first_name.trim(),
                  last_name: form.last_name.trim() || null,
                  email: form.email.trim() || null,
                  phone: form.phone.trim() || null,
                },
                {
                  onSuccess: (id) => {
                    setAdding(false)
                    onChange(id)
                  },
                },
              )
            }
          >
            Add client
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      </div>
    )

  return (
    <Field label="Client">
      <SearchMenu
        placeholder={loading ? 'Loading clients…' : 'Search your clients'}
        items={contacts.map((contact) => ({ id: contact.id, label: fullName(contact), ...(contact.email ? { detail: contact.email } : {}) }))}
        footer={<MenuAction icon="＋" label="Add new client" onClick={() => setAdding(true)} />}
        onPick={onChange}
      />
    </Field>
  )
}

function ProjectPicker({
  client,
  value,
  onChange,
}: {
  client: ContactWithJobs
  value: ProjectChoice | null
  onChange: (choice: ProjectChoice | null) => void
}) {
  const projects = client.jobs.filter((job) => !job.archived_at)
  const label = (job: ContactWithJobs['jobs'][number]) =>
    `${job.title?.trim() || `${client.first_name}'s ${JOB_KIND_LABEL[job.kind].toLowerCase()}`}`

  if (value?.mode === 'existing') {
    const job = client.jobs.find((item) => item.id === value.jobId)
    return (
      <Field label="Project">
        <div>
          <Chip text={job ? `${label(job)} · ${formatDateShort(job.event_date)}` : 'Project'} onClear={() => onChange(null)} />
        </div>
      </Field>
    )
  }
  if (value?.mode === 'none')
    return (
      <Field label="Project">
        <div>
          <Chip text="No project" onClear={() => onChange(null)} />
        </div>
      </Field>
    )
  if (value?.mode === 'new')
    return (
      <div className="flex flex-col gap-3">
        <Field label="Project">
          <div>
            <Chip text="Add new project" onClear={() => onChange(null)} />
          </div>
        </Field>
        <Field label="New project name">
          <Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="New project type">
            <Select value={value.kind} onChange={(e) => onChange({ ...value, kind: e.target.value as JobKind })}>
              {PROJECT_KINDS.map((key) => (
                <option key={key} value={key}>
                  {JOB_KIND_LABEL[key]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date" hint="The wedding or shoot date. It puts the project on the board and the calendar.">
            <Input type="date" value={value.date} onChange={(e) => onChange({ ...value, date: e.target.value as IsoDate })} />
          </Field>
        </div>
      </div>
    )

  return (
    <Field label="Project">
      <SearchMenu
        placeholder={projects.length ? 'Search their projects' : 'No projects yet'}
        items={projects.map((job) => ({ id: job.id, label: label(job), detail: formatDateShort(job.event_date) }))}
        footer={
          <>
            <MenuAction
              icon="＋"
              label="Add new project"
              onClick={() => onChange({ mode: 'new', name: `${client.first_name}'s Project`, kind: 'wedding', date: '' })}
            />
            <MenuAction icon="⊘" label="No project" onClick={() => onChange({ mode: 'none' })} />
          </>
        }
        onPick={(jobId) => onChange({ mode: 'existing', jobId })}
      />
    </Field>
  )
}
